import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import SearchableSelect from "@/components/SearchableSelect";

/**
 * Generic "Add New" inline dialog for masters within dropdowns.
 * fields: [{key, label, required, type?: "select", options?: [{id, label}], inputType?}]
 */
export default function AddNewDialog({
  open,
  onOpenChange,
  title,
  fields,
  onSubmit,
  submittingLabel = "Saving…",
  testIdPrefix = "add-new",
}) {
  const [values, setValues] = useState({});
  const [busy, setBusy] = useState(false);

  React.useEffect(() => {
    if (open) setValues({});
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await onSubmit(values);
      onOpenChange(false);
    } catch (err) {
      // keep dialog open if onSubmit throws (validation, server error, etc.)
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="rounded-none border vsm-border max-w-md"
        data-testid={`${testIdPrefix}-dialog`}
      >
        <DialogHeader>
          <div className="vsm-overline">Quick Add</div>
          <DialogTitle className="text-2xl font-bold tracking-tight">{title}</DialogTitle>
          <DialogDescription className="vsm-muted text-sm">
            Create a new entry without leaving the form.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 mt-2">
          {fields.map((f) => (
            <div key={f.key}>
              <Label className="vsm-overline">
                {f.label} {f.required && <span className="text-red-600">*</span>}
              </Label>
              <div className="mt-2">
                {f.type === "select" ? (
                  <SearchableSelect
                    testId={`${testIdPrefix}-${f.key}`}
                    value={values[f.key] || ""}
                    onChange={(v) => setValues((s) => ({ ...s, [f.key]: v }))}
                    options={f.options || []}
                    placeholder={f.placeholder || `Select ${f.label.toLowerCase()}`}
                  />
                ) : (
                  <Input
                    data-testid={`${testIdPrefix}-${f.key}-input`}
                    className="rounded-none"
                    value={values[f.key] || ""}
                    required={f.required}
                    type={f.inputType || "text"}
                    onChange={(e) => setValues((v) => ({ ...v, [f.key]: e.target.value }))}
                    autoFocus={f === fields[0]}
                  />
                )}
              </div>
            </div>
          ))}
          <DialogFooter className="pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              data-testid={`${testIdPrefix}-cancel`}
              className="rounded-none"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={busy}
              data-testid={`${testIdPrefix}-save`}
              className="rounded-none vsm-primary-bg hover:opacity-90"
            >
              {busy ? submittingLabel : "Save"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
